/**
 * BlogTab — the personal blog on the user profile (DEV-1381).
 *
 * This is the user-facing "Blog": personal expression — opinion, reflection,
 * commentary, stories. No editorial gatekeeping; blogs never appear in community
 * knowledge sections or news.
 *
 * ── Vocabulary (Skip ruling 2026-07-12, SPEC_PERSONAL_JOURNAL §12.2) ──
 * The surface says "Blog". The internal content type is `journal_entry`. The bare
 * word "Journal" belongs to the *Project* Journal (DEV-1295) and must never appear
 * here. If you are renaming this to "Journal", re-read the ruling first.
 *
 * ── What changed from the old version ──
 * It was localStorage-only (`l4e_blog_drafts`), "New Post" was a TODO that dumped
 * the author into the Knowledge window's multi-type wizard, and the cards were inert
 * <div>s that could not be opened. Now:
 *   - posts are server-persisted chronicles (template_key = 'journal_entry'),
 *   - "New Blog" creates a journal_entry and opens its editor DIRECTLY — no type
 *     picker, no /knowledge detour (single-type surface, §12.3),
 *   - cards OPEN the post: journal entries are chronicles, so the chronicle detail
 *     render IS the reader (§12.4). No parallel blog-reader page.
 *
 * Posts default to PRIVATE (server-enforced on create). The visibility badge below
 * is deliberately loud: an author who cannot tell a private post from a public one
 * has no way to know nobody is reading them (§12.6).
 *
 * Legacy localStorage drafts are still surfaced, clearly marked, with a one-click
 * migration onto the server — we do not strand text an author already wrote.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { mediaSrc } from '../../api/mediaUrl';
import {
  fetchChronicles,
  createJournalEntry,
  type ChronicleSummary,
} from '../../api/chronicles';
import { createSection } from '../../api/projectContentSections';
import { resolveTemplate } from '../../Data/Content/contentTemplates';
import type { BlogDraft } from '../../Data/Blog/blogDraftStore';
import styles from './BlogTab.module.css';

/** The chronicle detail render doubles as the blog reader/editor (§12.4). */
const readerPath = (chronicleGuid: string) => `/chronicles/${chronicleGuid}/view`;

/**
 * Is this chronicle a personal blog post?
 *
 * Resolved through the template registry rather than a hardcoded string set, so the
 * legacy `opinion` / `personal` category keys — which LEGACY_KEY_MAP now subsumes
 * into journal_entry — are matched too, without listing them again here.
 */
function isBlogPost(c: ChronicleSummary): boolean {
  if (c.templateKey === 'journal_entry') return true;
  return resolveTemplate(c.categoryKey)?.key === 'journal_entry';
}

// ── Legacy localStorage drafts (pre-DEV-1381) ───────────────────

const STORAGE_KEY = 'l4e_blog_drafts';
const LEGACY_BLOG_TYPES = new Set(['opinion', 'personal']);

function loadLegacyDrafts(isOwnProfile: boolean): BlogDraft[] {
  // Local drafts live in THIS browser — they are only ever the viewer's own.
  if (!isOwnProfile) return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const all = JSON.parse(raw) as BlogDraft[];
    return all.filter(
      (d) => LEGACY_BLOG_TYPES.has(d.blogType) && d.status !== 'archived',
    );
  } catch {
    return []; // corrupt payload — never let it break the tab
  }
}

function dropLegacyDraft(draftId: string): void {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const all = JSON.parse(raw) as BlogDraft[];
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(all.filter((d) => d.draftId !== draftId)),
    );
  } catch {
    /* best-effort cleanup — the post is already safely on the server */
  }
}

// ── Name-your-post dialog ───────────────────────────────────────

interface NameBlogDialogProps {
  title: string;
  onTitleChange: (v: string) => void;
  onCreate: () => void;
  onCancel: () => void;
  busy: boolean;
  error: string | null;
}

/**
 * A single "name it" step before the editor opens.
 *
 * Deliberately one field. This is not the type wizard SPEC §12.3 bans — there is no
 * type to choose on a single-type surface. It exists because the chronicle detail
 * page has no inline title editing (the title lives in ChronicleSettingsModal →
 * Basics, behind the gear), so without this an author lands on a post called
 * "Untitled post" with no visible way to rename it. The hint says so out loud rather
 * than leaving them to hunt for the gear.
 *
 * Styling mirrors CreateChronicleModal step 2 so the create grammar is identical
 * across content types. Background is composited opaque per CLAUDE.md — --bg-surface
 * alone is a transparent overlay tint and would render see-through.
 */
function NameBlogDialog({
  title,
  onTitleChange,
  onCreate,
  onCancel,
  busy,
  error,
}: NameBlogDialogProps) {
  const { t } = useAppTranslation('userProfile');

  const overlay: React.CSSProperties = {
    position: 'fixed',
    inset: 0,
    zIndex: 1000,
    background: 'rgba(0,0,0,0.6)',
    backdropFilter: 'blur(2px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
  };
  const card: React.CSSProperties = {
    width: 'min(460px, 100%)',
    borderRadius: 12,
    border: '1px solid var(--border-default)',
    backgroundColor: 'var(--bg-primary)',
    backgroundImage: 'linear-gradient(var(--bg-elevated), var(--bg-elevated))',
    boxShadow: 'var(--shadow-lg)',
  };
  const field: React.CSSProperties = {
    width: '100%',
    padding: '9px 11px',
    borderRadius: 8,
    border: '1px solid var(--border-default)',
    background: 'var(--bg-primary)',
    color: 'var(--text-primary)',
    fontSize: 14,
  };

  return (
    <div style={overlay} onClick={onCancel}>
      <div style={card} onClick={(e) => e.stopPropagation()}>
        <div
          style={{
            padding: '16px 18px',
            borderBlockEnd: '1px solid var(--border-subtle)',
          }}
        >
          <h2 style={{ margin: 0, fontSize: 16, color: 'var(--text-primary)' }}>
            {t('blog.nameHeading')}
          </h2>
        </div>

        <div
          style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 8 }}
        >
          <label
            style={{ fontSize: 13, color: 'var(--text-tertiary)' }}
            htmlFor="blog-title"
          >
            {t('blog.nameLabel')}
          </label>
          <input
            id="blog-title"
            autoFocus
            style={field}
            value={title}
            onChange={(e) => onTitleChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') onCreate();
            }}
            placeholder={t('blog.namePlaceholder')}
          />
          {/* Renaming is non-obvious — say it here, not after they're stuck. */}
          <p style={{ margin: 0, fontSize: 12, color: 'var(--text-muted)' }}>
            {t('blog.nameHint')}
          </p>
          {error && (
            <div style={{ color: 'var(--color-error)', fontSize: 13 }} role="alert">
              {error}
            </div>
          )}
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 8,
            padding: '14px 18px',
            borderBlockStart: '1px solid var(--border-subtle)',
          }}
        >
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            style={{
              padding: '8px 16px',
              borderRadius: 8,
              border: '1px solid var(--border-default)',
              background: 'transparent',
              color: 'var(--text-secondary)',
              cursor: 'pointer',
              fontWeight: 600,
            }}
          >
            {t('blog.cancel')}
          </button>
          <button
            type="button"
            onClick={onCreate}
            disabled={busy}
            style={{
              padding: '8px 16px',
              borderRadius: 8,
              border: 0,
              background: 'var(--accent-primary)',
              color: '#fff',
              cursor: busy ? 'wait' : 'pointer',
              fontWeight: 600,
            }}
          >
            {busy ? t('blog.creating') : t('blog.create')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Props ───────────────────────────────────────────────────────

interface Props {
  userGuid: string;
  isOwnProfile: boolean;
  displayName?: string;
}

// ── Component ───────────────────────────────────────────────────

export default function BlogTab({ userGuid, isOwnProfile }: Props) {
  const { t } = useAppTranslation('userProfile');
  const navigate = useNavigate();

  const [posts, setPosts] = useState<ChronicleSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Name-first create. The chronicle detail page has NO inline title editing — the
  // title lives behind the gear (ChronicleSettingsModal → Basics) — so creating an
  // "Untitled post" and dropping the author into the editor would strand every post
  // under a name they cannot obviously change. Ask for the name up front, and say
  // out loud that it is not permanent.
  //
  // This is NOT the type wizard §12.3 bans: there is no type to pick (single-type
  // surface). It is the same "name it" beat as CreateChronicleModal step 2, so the
  // create grammar stays consistent across content types.
  const [naming, setNaming] = useState(false);
  const [newTitle, setNewTitle] = useState('');

  const [legacyDrafts, setLegacyDrafts] = useState<BlogDraft[]>(() =>
    loadLegacyDrafts(isOwnProfile),
  );

  // ── Load the author's blog posts ──
  // The server filters by initiator and enforces visibility, so a visitor only ever
  // receives the posts they are allowed to see.
  const load = useCallback(async () => {
    if (!userGuid) return;
    setLoading(true);
    try {
      const result = await fetchChronicles({
        InitiatorGuid: userGuid,
        PageSize: '100',
      });
      setPosts((result.items ?? []).filter(isBlogPost));
    } catch {
      setPosts([]);
    } finally {
      setLoading(false);
    }
  }, [userGuid]);

  useEffect(() => {
    let alive = true;
    (async () => {
      await load();
      if (!alive) return;
    })();
    return () => {
      alive = false;
    };
  }, [load]);

  // ── "New Blog" — name it, then straight into the editor (§12.3) ──
  // Creates the draft, then lands the author on the chronicle detail page, which is
  // the editor. Visibility is NOT sent: the server defaults journal_entry to private
  // so the default fails closed even if this bundle is stale.
  const handleCreate = useCallback(async () => {
    if (busy) return;
    const title = newTitle.trim();
    if (!title) {
      setError(t('blog.errorTitleRequired'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const created = await createJournalEntry(title);
      navigate(readerPath(created.chronicleGuid));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('blog.createError'));
      setBusy(false);
    }
  }, [busy, newTitle, navigate, t]);

  const openNaming = useCallback(() => {
    setNewTitle('');
    setError(null);
    setNaming(true);
  }, []);

  // ── Migrate a legacy localStorage draft onto the server ──
  // Body text is carried across as a content section so nothing the author wrote is
  // lost; the draft is only removed from localStorage after the server has it.
  const handleMigrate = useCallback(
    async (draft: BlogDraft) => {
      if (busy) return;
      setBusy(true);
      setError(null);
      try {
        const title = draft.headline?.trim() || t('blog.untitled');
        const created = await createJournalEntry(title);

        if (draft.bodyJson || draft.bodyText) {
          await createSection('chronicle', created.chronicleGuid, {
            title,
            bodyJson: draft.bodyJson || undefined,
            bodyPlain: draft.bodyText || undefined,
          });
        }

        dropLegacyDraft(draft.draftId);
        setLegacyDrafts((prev) => prev.filter((d) => d.draftId !== draft.draftId));
        navigate(readerPath(created.chronicleGuid));
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : t('blog.migrateError'));
        setBusy(false);
      }
    },
    [busy, navigate, t],
  );

  const sortedPosts = useMemo(
    () =>
      [...posts].sort(
        (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
      ),
    [posts],
  );

  const isEmpty = sortedPosts.length === 0 && legacyDrafts.length === 0;

  /** Who can actually see this post — the answer the author most needs. */
  const visibilityLabel = (visibility: string): string => {
    if (visibility === 'public') return t('blog.visibilityPublic');
    if (visibility === 'community') return t('blog.visibilityCommunity');
    return t('blog.visibilityPrivate');
  };

  // ── Loading ──

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <div>
            <h3 className={styles.title}>{t('blog.title')}</h3>
            <p className={styles.subtitle}>{t('blog.subtitle')}</p>
          </div>
        </div>
        <div className={styles.emptyState}>
          <p className={styles.emptyDesc}>{t('knowledge.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div>
          <h3 className={styles.title}>{t('blog.title')}</h3>
          <p className={styles.subtitle}>{t('blog.subtitle')}</p>
        </div>
        {isOwnProfile && (
          <button
            type="button"
            className={styles.newPostBtn}
            onClick={openNaming}
            disabled={busy}
          >
            {t('blog.newBlog')}
          </button>
        )}
      </div>

      {naming && (
        <NameBlogDialog
          title={newTitle}
          onTitleChange={setNewTitle}
          onCreate={handleCreate}
          onCancel={() => setNaming(false)}
          busy={busy}
          error={error}
        />
      )}

      {!naming && error && (
        <p style={{ color: 'var(--color-error)', fontSize: 13 }} role="alert">
          {error}
        </p>
      )}

      {/* Posts */}
      {sortedPosts.length > 0 && (
        <div className={styles.cardGrid}>
          {sortedPosts.map((post) => (
            <button
              key={post.chronicleGuid}
              type="button"
              className={post.heroImageUrl ? styles.cardWithHero : styles.card}
              onClick={() => navigate(readerPath(post.chronicleGuid))}
            >
              {post.heroImageUrl && (
                <div className={styles.heroWrap}>
                  <img
                    src={mediaSrc(post.heroImageUrl) ?? undefined}
                    alt=""
                    className={styles.hero}
                  />
                </div>
              )}
              <div className={styles.cardBody}>
                <div className={styles.cardHeader}>
                  <span
                    className={
                      post.status === 'published'
                        ? styles.statusPublished
                        : styles.statusDraft
                    }
                  >
                    {post.status === 'published'
                      ? t('blog.published')
                      : t('blog.draft')}
                  </span>
                  {/* Who can see it — loud on purpose (§12.6). */}
                  {isOwnProfile && (
                    <span className={styles.statusDraft}>
                      {visibilityLabel(post.visibility)}
                    </span>
                  )}
                </div>
                <h4 className={styles.cardTitle}>{post.title}</h4>
                {post.subtitle && (
                  <p className={styles.cardExcerpt}>{post.subtitle}</p>
                )}
                <div className={styles.cardMeta}>
                  <span>{new Date(post.updatedAt).toLocaleDateString()}</span>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Legacy local drafts — never silently dropped */}
      {legacyDrafts.length > 0 && (
        <div className={styles.cardGrid}>
          {legacyDrafts.map((draft) => (
            <div key={draft.draftId} className={styles.card}>
              <div className={styles.cardBody}>
                <div className={styles.cardHeader}>
                  <span className={styles.statusDraft}>{t('blog.localDraft')}</span>
                </div>
                <h4 className={styles.cardTitle}>
                  {draft.headline || t('blog.untitled')}
                </h4>
                {draft.bodyText && (
                  <p className={styles.cardExcerpt}>
                    {draft.bodyText.slice(0, 120)}
                    {draft.bodyText.length > 120 ? '…' : ''}
                  </p>
                )}
                <p className={styles.cardExcerpt}>{t('blog.localDraftHint')}</p>
                <button
                  type="button"
                  className={styles.newPostBtn}
                  onClick={() => handleMigrate(draft)}
                  disabled={busy}
                >
                  {t('blog.migrateDraft')}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Empty */}
      {isEmpty && (
        <div className={styles.emptyState}>
          <svg
            width="44"
            height="44"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            style={{ color: 'var(--text-muted)' }}
          >
            <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
          </svg>
          <h3 className={styles.emptyTitle}>
            {isOwnProfile ? t('blog.emptyOwn') : t('blog.emptyOther')}
          </h3>
          <p className={styles.emptyDesc}>
            {isOwnProfile ? t('blog.emptyOwnDesc') : t('blog.emptyOtherDesc')}
          </p>
        </div>
      )}
    </div>
  );
}
